import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import './DealDatePicker.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function parseDateValue(value) {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toDateValue(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDisplay(value) {
  const date = parseDateValue(value);
  if (!date) return '';
  return date.toLocaleDateString('en-PK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function isSameDay(a, b) {
  return (
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export default function DealDatePicker({
  value,
  onChange,
  placeholder = 'Select date',
  min,
  max,
  disabled = false,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  const selectedDate = parseDateValue(value);
  const minDate = parseDateValue(min);
  const maxDate = parseDateValue(max);

  const [viewDate, setViewDate] = useState(() => selectedDate || new Date());

  useEffect(() => {
    if (selectedDate) {
      setViewDate(selectedDate);
    }
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const calendarDays = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startOffset = firstDay.getDay();
    const gridStart = new Date(year, month, 1 - startOffset);

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);
      return date;
    });
  }, [viewDate]);

  const isDisabledDate = (date) => {
    const day = startOfDay(date);
    if (minDate && day < startOfDay(minDate)) return true;
    if (maxDate && day > startOfDay(maxDate)) return true;
    return false;
  };

  const handleSelect = (date) => {
    if (isDisabledDate(date)) return;
    onChange(toDateValue(date));
    setOpen(false);
  };

  const goMonth = (delta) => {
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange('');
    setOpen(false);
  };

  return (
    <div className={`deal-date-picker${disabled ? ' deal-date-picker--disabled' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="deal-date-picker__trigger"
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <FaCalendarAlt className="deal-date-picker__icon" aria-hidden="true" />
        <span className={value ? 'deal-date-picker__value' : 'deal-date-picker__placeholder'}>
          {value ? formatDisplay(value) : placeholder}
        </span>
        {value && !disabled && (
          <span
            role="button"
            tabIndex={0}
            className="deal-date-picker__clear"
            onClick={handleClear}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClear(e);
              }
            }}
            aria-label="Clear date"
          >
            <FaTimes size={12} />
          </span>
        )}
      </button>

      {open && (
        <div className="deal-date-picker__popover" role="dialog" aria-label="Choose date">
          <div className="deal-date-picker__header">
            <button type="button" className="deal-date-picker__nav" onClick={() => goMonth(-1)} aria-label="Previous month">
              <FaChevronLeft />
            </button>
            <span className="deal-date-picker__month">
              {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
            </span>
            <button type="button" className="deal-date-picker__nav" onClick={() => goMonth(1)} aria-label="Next month">
              <FaChevronRight />
            </button>
          </div>

          <div className="deal-date-picker__weekdays">
            {WEEKDAYS.map((day) => (
              <span key={day} className="deal-date-picker__weekday">
                {day}
              </span>
            ))}
          </div>

          <div className="deal-date-picker__grid">
            {calendarDays.map((date) => {
              const inMonth = date.getMonth() === viewDate.getMonth();
              const selected = isSameDay(date, selectedDate);
              const today = isSameDay(date, new Date());
              const disabledDay = isDisabledDate(date);

              return (
                <button
                  key={toDateValue(date)}
                  type="button"
                  className={[
                    'deal-date-picker__day',
                    !inMonth && 'deal-date-picker__day--muted',
                    selected && 'deal-date-picker__day--selected',
                    today && 'deal-date-picker__day--today',
                    disabledDay && 'deal-date-picker__day--disabled',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handleSelect(date)}
                  disabled={disabledDay}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="deal-date-picker__footer">
            <button
              type="button"
              className="deal-date-picker__today-btn"
              onClick={() => {
                const today = new Date();
                if (!isDisabledDate(today)) {
                  handleSelect(today);
                }
              }}
            >
              Today
            </button>
            {value && (
              <button type="button" className="deal-date-picker__clear-btn" onClick={handleClear}>
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
